/**
 * ESC/POS, the bytes a thermal printer actually understands
 * (`08-pos-terminal-and-qr.md` § Devices, `03-data-model.md` § 8).
 *
 * The layer above this decided *what* a receipt says and *whether* it goes as
 * text or as an image; this decides nothing at all. That separation is the
 * point: a merchant's on-screen preview and the paper that comes out of the
 * printer are the same lines, and the only thing that varies is the transport.
 *
 * Two facts about these printers shape everything here:
 *
 *  - **A thermal printer's text mode has a code page, not Unicode.** Khmer is
 *    not in any of them, and neither is most of what this product will meet.
 *    That is why the raster path exists, and why `decideRenderMode` forces it
 *    for the scripts a page cannot hold rather than leaving it to a setting
 *    somebody forgets. It is *not* keyed on a script being complex: Thai is
 *    every bit as complex as Khmer and prints as bytes, because the printer
 *    carries a table for it. The locale registry states which is which.
 *  - **A raster image is markedly slower.** A queue of Latin receipts printed
 *    as images is a queue that falls behind a Friday night, so the text path
 *    stays the default where it is safe.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

/**
 * `ESC t 16` — WPC1252, the code page the bytes `text()` writes actually mean.
 *
 * `text()` emits one byte per code point below 0x100, which is Latin-1. A
 * printer does not know that: `ESC @` resets it to whatever its DIP switches
 * say, and that is CP437 on nearly every unit sold, where 0xE9 is "Θ" rather
 * than "é". So a receipt with a French or Vietnamese name on it printed
 * mojibake, and would have printed mojibake in production, because there is no
 * way to notice from the byte stream alone.
 *
 * WPC1252 rather than PC858 or ISO-8859-1: it agrees with Latin-1 across
 * 0xA0–0xFF, it is what a Windows-derived POS ecosystem has standardised on,
 * and it is the most widely implemented of the three. `decideRenderMode` still
 * forces the raster path for anything outside the page — this is only about
 * the characters text mode can actually carry.
 */
const CODE_PAGE_WPC1252 = 16;

/**
 * `ESC t 26` — Thai Character Code 18, and the reason a Thai receipt does not
 * need the raster path.
 *
 * Epson ships seven Thai tables (pages 20–26). They differ in how many passes
 * the head makes to stack a vowel or a tone mark over its consonant, **not** in
 * where the characters sit: all of them carry the Thai block at TIS-620's
 * positions, so the byte a character encodes to is the same on any of them and
 * only the page number is a choice. 26 is the one-pass table, which is what the
 * `THAI 1 PASS` model font is for.
 *
 * **Unverified against hardware.** There is no printer here, and which of the
 * seven a given unit is expecting is a property of that unit. A merchant whose
 * printer disagrees gets stacked marks in the wrong place rather than mojibake,
 * because the mapping is shared — and the fix is this number. If that turns out
 * to happen, it becomes a printer setting; inventing one now would be a
 * configuration option nobody has yet needed.
 */
const CODE_PAGE_THAI = 26;

const QUESTION_MARK = 0x3f;

/** The first and last Thai code points TIS-620 carries. */
const THAI_FIRST = 0x0e01;
const THAI_LAST = 0x0e5b;

/**
 * Which page carries a character, and what byte it becomes there.
 *
 * A page of `null` means no page selection is needed: either the character is
 * ASCII, which sits identically in every table this writer selects, or no table
 * carries it at all and it becomes a question mark. Both cases must leave the
 * active page alone — a receipt that switched pages to print a space would
 * spend more bytes on `ESC t` than on text.
 */
function encodeCharacter(code: number): { page: number | null; byte: number } {
  if (code < 0x80) return { page: null, byte: code };
  if (code < 0x100) return { page: CODE_PAGE_WPC1252, byte: code };
  // TIS-620 places the Thai block linearly at 0xA0 + the low byte, gap and all,
  // so the mapping is arithmetic rather than a table.
  if (code >= THAI_FIRST && code <= THAI_LAST) {
    return { page: CODE_PAGE_THAI, byte: code - 0x0e00 + 0xa0 };
  }
  return { page: null, byte: QUESTION_MARK };
}

export type Alignment = "left" | "centre" | "right";

/** Everything a caller can ask for, as data rather than as byte-poking. */
export type PrintCommand =
  | { readonly kind: "text"; readonly value: string; readonly align?: Alignment }
  | { readonly kind: "emphasis"; readonly on: boolean }
  | { readonly kind: "double"; readonly on: boolean }
  | { readonly kind: "feed"; readonly lines: number }
  | { readonly kind: "cut" }
  | { readonly kind: "drawer" }
  | {
      readonly kind: "raster";
      /** One bit per pixel, row-major, already padded to whole bytes. */
      readonly bits: Uint8Array;
      readonly widthPx: number;
      readonly heightPx: number;
    }
  | { readonly kind: "qr"; readonly value: string; readonly size?: number };

export class EscPosWriter {
  #chunks: number[] = [];
  /**
   * The page the printer is on. Tracked rather than assumed, because `text()`
   * switches pages mid-string and every switch has to be undone before the next
   * Latin character or the receipt's own totals come out as Thai.
   */
  #activePage = CODE_PAGE_WPC1252;

  /**
   * `ESC @` — the only safe assumption about a printer's state.
   *
   * Followed immediately by a code page selection, because `ESC @` is what
   * *creates* the problem: it returns the printer to its own default, which is
   * not the one these bytes are written for.
   */
  initialise(): this {
    this.#chunks.push(ESC, 0x40);
    return this.codePage(CODE_PAGE_WPC1252);
  }

  /** `ESC t n` — which character set the bytes after this one mean. */
  codePage(page: number): this {
    this.#activePage = page & 0xff;
    this.#chunks.push(ESC, 0x74, this.#activePage);
    return this;
  }

  align(alignment: Alignment): this {
    this.#chunks.push(ESC, 0x61, alignment === "left" ? 0 : alignment === "centre" ? 1 : 2);
    return this;
  }

  emphasis(on: boolean): this {
    this.#chunks.push(ESC, 0x45, on ? 1 : 0);
    return this;
  }

  /** Double height and width, for the one line a customer actually reads. */
  double(on: boolean): this {
    this.#chunks.push(GS, 0x21, on ? 0x11 : 0x00);
    return this;
  }

  /**
   * Code-page bytes rather than UTF-8: a text-mode printer reads a page, never
   * Unicode.
   *
   * The page is chosen per character and switched only when it has to change,
   * which is what lets one line carry a Thai dish name and a Latin price
   * without the caller knowing either is there. ASCII never forces a switch,
   * because every page this writer selects agrees below 0x80.
   *
   * A character no page carries still becomes a question mark — that is what
   * `decideRenderMode` sends down the raster path instead, and it is why Khmer
   * and Thai take different routes to paper despite both being complex scripts.
   */
  text(value: string): this {
    for (const character of value) {
      const { page, byte } = encodeCharacter(character.codePointAt(0) ?? QUESTION_MARK);
      if (page !== null && page !== this.#activePage) this.codePage(page);
      this.#chunks.push(byte);
    }
    return this;
  }

  line(value = ""): this {
    return this.text(value).feed(1);
  }

  feed(lines: number): this {
    for (let index = 0; index < Math.max(0, lines); index++) this.#chunks.push(LF);
    return this;
  }

  /** `GS V 66` — a partial cut, which is what every till roll wants. */
  cut(): this {
    this.#chunks.push(GS, 0x56, 66, 0x00);
    return this;
  }

  /**
   * `ESC p` — the drawer kick.
   *
   * `08` § Cash drawer: opening the drawer is "the classic theft vector", so
   * this is only ever reached through a route that logs it. The pulse is on
   * pin 2, which is what nearly every drawer is wired to.
   */
  openDrawer(): this {
    this.#chunks.push(ESC, 0x70, 0x00, 0x19, 0xfa);
    return this;
  }

  /**
   * `GS v 0` — a raster bit image.
   *
   * The path Khmer, Burmese and Lao receipts take, because none of them exist
   * in a printer's code page. Thai is not among them — Epson ships seven Thai
   * code pages (20–26) and a `THAI 1 PASS` model font, so Thai prints as bytes.
   * The caller supplies bits; how they were drawn is the shell's business, and
   * keeping that out of here is what lets this run in a test with no canvas at
   * all.
   */
  raster(bits: Uint8Array, widthPx: number, heightPx: number): this {
    const bytesPerRow = Math.ceil(widthPx / 8);
    if (bits.length < bytesPerRow * heightPx) {
      throw new RangeError(
        `raster: expected ${bytesPerRow * heightPx} bytes for ${widthPx}×${heightPx}, got ${bits.length}`,
      );
    }

    this.#chunks.push(
      GS,
      0x76,
      0x30,
      0x00,
      bytesPerRow & 0xff,
      (bytesPerRow >> 8) & 0xff,
      heightPx & 0xff,
      (heightPx >> 8) & 0xff,
    );
    for (let index = 0; index < bytesPerRow * heightPx; index++) {
      this.#chunks.push(bits[index] ?? 0);
    }
    return this;
  }

  /**
   * A QR code, printed by the printer rather than rendered by us.
   *
   * Every ESC/POS printer worth buying does this in firmware, and a firmware
   * QR is sharper than a rasterised one at the same size.
   */
  qr(value: string, size = 6): this {
    const bytes = [...value].map((character) => character.codePointAt(0) ?? 0x3f);
    const length = bytes.length + 3;

    // Model 2, then the module size, then the error correction level.
    this.#chunks.push(GS, 0x28, 0x6b, 4, 0, 0x31, 0x41, 0x32, 0x00);
    this.#chunks.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x43, Math.min(16, Math.max(1, size)));
    this.#chunks.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x45, 0x31);
    this.#chunks.push(GS, 0x28, 0x6b, length & 0xff, (length >> 8) & 0xff, 0x31, 0x50, 0x30);
    this.#chunks.push(...bytes);
    this.#chunks.push(GS, 0x28, 0x6b, 3, 0, 0x31, 0x51, 0x30);
    return this;
  }

  bytes(): Uint8Array {
    return Uint8Array.from(this.#chunks);
  }
}

/**
 * Turns a list of commands into bytes.
 *
 * A list rather than a builder chain at the call site, so a print job can be
 * queued, stored, inspected in a test and replayed offline without carrying a
 * writer around with it.
 */
export function encode(commands: readonly PrintCommand[]): Uint8Array {
  const writer = new EscPosWriter().initialise();

  for (const command of commands) {
    switch (command.kind) {
      case "text":
        if (command.align) writer.align(command.align);
        writer.line(command.value);
        if (command.align && command.align !== "left") writer.align("left");
        break;
      case "emphasis":
        writer.emphasis(command.on);
        break;
      case "double":
        writer.double(command.on);
        break;
      case "feed":
        writer.feed(command.lines);
        break;
      case "cut":
        // Paper past the cutter first, or the cut lands in the middle of the
        // last line a customer wanted to read.
        writer.feed(3).cut();
        break;
      case "drawer":
        writer.openDrawer();
        break;
      case "raster":
        writer
          .align("centre")
          .raster(command.bits, command.widthPx, command.heightPx)
          .align("left");
        break;
      case "qr":
        writer.align("centre").qr(command.value, command.size).align("left");
        break;
    }
  }

  return writer.bytes();
}

/**
 * A rendered receipt as commands.
 *
 * The text path prints the lines the layout produced. The raster path prints
 * the image the shell drew of those same lines — which is why the caller hands
 * both in, and why nothing here re-lays-out anything.
 */
export function receiptCommands(input: {
  lines: readonly string[];
  mode: "text" | "raster";
  raster?: { bits: Uint8Array; widthPx: number; heightPx: number };
  qr?: string | null;
  openDrawer?: boolean;
}): PrintCommand[] {
  const commands: PrintCommand[] = [];

  if (input.mode === "raster") {
    if (!input.raster) {
      throw new Error("receiptCommands: the raster path needs an image to print.");
    }
    commands.push({ kind: "raster", ...input.raster });
  } else {
    for (const line of input.lines) commands.push({ kind: "text", value: line });
  }

  if (input.qr) commands.push({ kind: "feed", lines: 1 }, { kind: "qr", value: input.qr });
  if (input.openDrawer) commands.push({ kind: "drawer" });
  commands.push({ kind: "cut" });

  return commands;
}
